import { CSSProperties, memo, useMemo } from 'react';
import { ColumnRowProps } from '../../ColumnRowProps';
import { useGridContext } from '../../../../../../../_grid/contexts/gridContext';
import { useFixedCellValue } from '../../../../../../../_grid/hooks/useFixedCellValue';
import { CELL_HEIGHT, CELL_WIDTH } from '../../../../CONSTANTS';
import { GridCell } from '../../../../../../../../../api/generated/data-contracts';
import { useCategoryResourceAllocationBackground } from '../../../../hooks/useCategoryResourceAllocationBackground';
import { bigZero } from '../../../../../../../../../helpers/math';
import { ResourceType } from '../../../../../../../../../Entities/ResourceType';

export const ProjectColumnRow = memo(function ProjectColumnRow({ columnRow, rowId }: ColumnRowProps) {
    const render = useMemo(() => {
        return columnRow.cells.map((cell, i) => {
            return <ProjectCell key={cell.id} cell={cell} className={i === 0 ? 'Request' : 'Allocation'} cellIndex={i} rowId={rowId} />;
        });
    }, [columnRow.cells, rowId]);
    return <ColumnGridContainer>{render}</ColumnGridContainer>;
});

const ColumnGridContainer = (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
    const style = useMemo(() => {
        return {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            padding: '0px 10px',
            gap: 10,
            alignItems: 'center',
            height: '100%',
        } as CSSProperties;
    }, []);
    return <div style={style} {...props} />;
};

const ProjectCell = ({ cell, className, cellIndex, rowId }: { cell: GridCell; className: string; cellIndex: number; rowId: string }) => {
    const { useGridCellValue, getRowAncestorTreeToRoot } = useGridContext();
    const storeValue = useGridCellValue(cell);
    const value = useFixedCellValue(storeValue);
    const hasCategoryAllocation = useMemo(
        () =>
            // Check if root row is a category resource
            getRowAncestorTreeToRoot(rowId).at(-1).metadata?.ResourceType === ResourceType.Category &&
            // Check if the value is not zero
            !storeValue.eq(bigZero) &&
            // Check that this cell is an allocation cell
            cellIndex === 1,
        [cellIndex, getRowAncestorTreeToRoot, rowId, storeValue],
    );
    const backgroundColor = useCategoryResourceAllocationBackground(10);
    const styles = useMemo((): CSSProperties => {
        return {
            paddingRight: 8,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'end',
            height: CELL_HEIGHT,
            width: CELL_WIDTH,
            ...(hasCategoryAllocation && {
                backgroundImage: backgroundColor,
                backgroundPosition: '13px -8px',
            }),
        };
    }, [backgroundColor, hasCategoryAllocation]);
    return (
        <div style={styles} className={className}>
            {value}
        </div>
    );
};
